<template>
  <date-picker
    ref="picker"
    class="form-control app-datepicker-inner"
    :class="stateClass"
    :value="internalValue"
    type="date"
    :editable="false"
    :clearable="clearable"
    :format="resolvedFormat"
    value-type="date"
    :disabled="disabled"
    :disabled-date="disabledDateFn"
    :placeholder="resolvedPlaceholder"
    input-class="app-datepicker-input"
    v-bind="$attrs"
    @input="onInput"
    @blur="$emit('blur', $event)"
  >
    <template #icon-calendar>
      <font-awesome-icon class="icon-calendar-regular" :icon="['far', 'calendar']" />
      <font-awesome-icon class="icon-calendar-solid" :icon="['fas', 'calendar']" />
    </template>
  </date-picker>
</template>

<script lang="ts">
import 'vue2-datepicker/locale/ro'
import { Component, Prop, Vue } from 'vue-property-decorator'
import { toIsoDateString } from '@/utils/date-utils'
import DatePicker from 'vue2-datepicker'

export const DATE_FORMATS = {
  // 03.06.2026
  DATE_SHORT: 'DD.MM.YYYY',
  // miercuri, 3 iunie 2026
  DATE_HUGE: 'dddd, D MMMM YYYY'
} as const

@Component({
  name: 'AppDatepicker',
  inheritAttrs: false,
  components: { DatePicker }
})
export default class AppDatepicker extends Vue {
  @Prop({ default: null }) value!: Date | string | null
  @Prop({ default: null }) min!: Date | string | null
  @Prop({ default: null }) max!: Date | string | null
  @Prop({ type: String, default: null }) placeholder!: string | null
  @Prop({ type: Boolean, default: false }) clearable!: boolean
  @Prop({ type: Boolean, default: false }) disabled!: boolean
  @Prop({ type: String, default: 'DATE_SHORT' }) format!: string
  @Prop({ default: null }) state!: boolean | null
  @Prop({ type: String, default: 'date' }) valueType!: 'date' | 'string'

  get internalValue(): Date | null {
    if (!this.value) return null
    if (this.value instanceof Date) return this.value
    const d = new Date(this.value)
    return isNaN(d.getTime()) ? null : d
  }

  get resolvedFormat(): string {
    return (DATE_FORMATS as Record<string, string>)[this.format] ?? this.format
  }

  get resolvedPlaceholder(): string {
    return this.placeholder ?? this.$t('common.datepicker.labels.labelNoDateSelected') as string
  }

  get stateClass(): Record<string, boolean> {
    return {
      'is-valid': this.state === true,
      'is-invalid': this.state === false,
      'app-datepicker-disabled': this.disabled
    }
  }

  private toDateOnly(val: Date | string | null): Date | null {
    if (!val) return null
    const d = val instanceof Date ? val : new Date(val)
    return isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate())
  }

  get disabledDateFn(): (date: Date) => boolean {
    return (date: Date) => {
      const d = new Date(date.getFullYear(), date.getMonth(), date.getDate())
      const min = this.toDateOnly(this.min)
      const max = this.toDateOnly(this.max)
      if (min && d < min) return true
      if (max && d > max) return true
      return false
    }
  }

  onInput(date: Date | null): void {
    if (this.valueType === 'string') {
      this.$emit('input', date ? toIsoDateString(date) : null)
    } else {
      this.$emit('input', date)
    }
  }

  focus(): void {
    const input = this.$el.querySelector<HTMLInputElement>('input')
    if (input) {
      input.focus()
    }
  }

  closePopup(): void {
    (this.$refs.picker as any)?.closePopup?.()
  }

  openPopup(): void {
    (this.$refs.picker as any)?.openPopup?.()
  }
}
</script>

<style lang="scss" scoped>
@import '~@/project-bootstrap-vars';
@import '~bootstrap/scss/functions';
@import '~bootstrap/scss/variables';

/* vue2-datepicker ships .mx-datepicker { display:inline-block; width:210px }
   which loads after Bootstrap and wins the cascade. Our scoped rule (higher
   specificity via [data-v-*]) re-asserts block + full-width for standalone use.
   Height is pinned to Bootstrap's exact form-control value so the field matches
   other inputs on the page (height:auto can grow slightly due to font rounding). */
.app-datepicker-inner.form-control {
  display: block;
  width: 100%;
  padding: 0;
  height: $input-height;
}

/* Stretch the inner wrappers so the input fills the fixed-height outer shell */
.app-datepicker-inner ::v-deep .mx-input-wrapper {
  height: 100%;
}

/* Mirror Bootstrap's .form-control:disabled background for the div-based wrapper */
.app-datepicker-inner.app-datepicker-disabled {
  background-color: $input-disabled-bg;
}

/* Move calendar icon to the left */
.app-datepicker-inner ::v-deep .mx-icon-calendar {
  right: auto;
  left: $input-padding-x;
}

/* Inner input is transparent — border/background/shadow live on the outer wrapper.
   [readonly] is intentionally kept (vue2-datepicker sets it via :editable="false" to
   block keyboard input); we just undo Bootstrap's grey-background rule for it.
   Extra left padding leaves room for the calendar icon. */
.app-datepicker-inner ::v-deep .app-datepicker-input {
  width: 100%;
  height: 100%;
  border: 0;
  outline: none;
  box-shadow: none;
  background: transparent;
  padding: $input-padding-y $input-padding-x;
  padding-left: $input-padding-x * 2 + 1rem;
  cursor: pointer;

  &[readonly] {
    background: transparent;
  }
}

</style>
